/**
 * Aisle & After — Cloudinary Direct Upload Helper
 *
 * Flow:
 *  1. GET  /api/photos/signature  — backend returns signed upload params
 *  2. POST directly to Cloudinary — client uploads, backend never sees bytes
 *  3. POST /api/photos/confirm    — backend records the resulting URL
 */

import React from 'react';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Upload a photo directly to Cloudinary.
 *
 * @param {File}   file        - File object from <input type="file">
 * @param {string} authToken   - User's JWT
 * @param {(pct: number) => void} [onProgress] - Optional 0-100 progress callback
 * @returns {Promise<{ photo_url: string, file_id: string }>}
 */
export async function uploadPhoto(file, authToken, onProgress) {
  // Step 1: get signed params from backend
  const sigRes = await fetch(`${API_BASE}/photos/signature`, {
    headers: { Authorization: `Bearer ${authToken}` },
  });
  if (!sigRes.ok) {
    const err = await sigRes.json().catch(() => ({}));
    throw new Error(err.detail ?? "Failed to get upload signature");
  }
  const { signature, timestamp, folder, transformation, cloud_name, api_key } =
    await sigRes.json();

  // Step 2: upload directly to Cloudinary
  const formData = new FormData();
  formData.append("file", file);
  formData.append("api_key", api_key);
  formData.append("timestamp", String(timestamp));
  formData.append("signature", signature);
  formData.append("folder", folder);
  formData.append("transformation", transformation);

  const uploadUrl = `https://api.cloudinary.com/v1_1/${cloud_name}/image/upload`;

  const { secure_url, public_id } = await new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", uploadUrl);

    if (onProgress) {
      xhr.upload.addEventListener("progress", (e) => {
        if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
      });
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(JSON.parse(xhr.responseText));
      } else {
        const body = (() => { try { return JSON.parse(xhr.responseText); } catch { return {}; } })();
        reject(new Error(body?.error?.message ?? `Cloudinary upload failed (${xhr.status})`));
      }
    };
    xhr.onerror = () => reject(new Error("Network error during upload"));
    xhr.send(formData);
  });

  // Step 3: tell the backend the resulting URL
  const confirmRes = await fetch(`${API_BASE}/photos/confirm`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${authToken}`,
    },
    body: JSON.stringify({ secure_url, public_id }),
  });
  if (!confirmRes.ok) {
    const err = await confirmRes.json().catch(() => ({}));
    throw new Error(err.detail ?? "Failed to confirm photo upload");
  }
  return confirmRes.json();
}


/**
 * React hook — photo upload with progress.
 *
 * const { upload, uploading, progress, error } = usePhotoUpload(token, onSuccess);
 * <input type="file" accept="image/*" onChange={e => upload(e.target.files[0])} />
 */
export function usePhotoUpload(authToken, onSuccess) {
  const [uploading, setUploading] = React.useState(false);
  const [progress, setProgress]   = React.useState(0);
  const [error, setError]         = React.useState(null);

  const upload = React.useCallback(async (file) => {
    if (!file) return;
    setUploading(true);
    setProgress(0);
    setError(null);
    try {
      const result = await uploadPhoto(file, authToken, setProgress);
      onSuccess?.(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  }, [authToken, onSuccess]);

  return { upload, uploading, progress, error };
}


/**
 * React hook — WebSocket with Redis-backed delivery + auto-reconnect.
 *
 * Because the backend uses Redis pub/sub, messages arrive regardless of
 * which Render instance the sender is on.
 *
 * const { sendPong } = useMatchChat(matchId, token, onNewMessage);
 */
export function useMatchChat(matchId, authToken, onNewMessage) {
  const wsRef      = React.useRef(null);
  const retryCount = React.useRef(0);
  const maxRetries = 8;

  React.useEffect(() => {
    if (!matchId || !authToken) return;
    let timeout;

    function connect() {
      const wsBase = process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws");
      const ws = new WebSocket(`${wsBase}/ws/chat/${matchId}?token=${authToken}`);
      wsRef.current = ws;

      ws.onopen = () => { retryCount.current = 0; };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === "ping") {
            ws.send(JSON.stringify({ type: "pong" }));
            return;
          }
          if (data.type === "new_message") onNewMessage?.(data.message);
        } catch { /* ignore malformed frames */ }
      };

      ws.onclose = (e) => {
        if (e.code === 4001 || e.code === 4003) return; // auth failure — don't retry
        if (retryCount.current < maxRetries) {
          const delay = Math.min(1000 * 2 ** retryCount.current, 30_000);
          retryCount.current++;
          timeout = setTimeout(connect, delay);
        }
      };

      ws.onerror = () => ws.close();
    }

    connect();
    return () => { clearTimeout(timeout); wsRef.current?.close(1000); };
  }, [matchId, authToken]);

  const sendPong = React.useCallback(() => {
    wsRef.current?.send(JSON.stringify({ type: "pong" }));
  }, []);

  return { sendPong };
}
